import { useEffect, useRef } from 'react'
import { TextField } from '@mui/material'
import flatpickr from 'flatpickr'
import { French } from 'flatpickr/dist/l10n/fr.js'
import type { Instance } from 'flatpickr/dist/types/instance'
import 'flatpickr/dist/flatpickr.min.css'

/**
 * Sélecteur de date, sur flatpickr.
 *
 * ⭐ Extrait de `ChampDateNaissance`, qui l'était le premier et le reste : une
 * date de naissance est une date bornée autrement, pas une autre mécanique. Le
 * jour où le calendrier gagne une option, les deux usages en profitent — et le
 * jour où il faut corriger un défaut, il y a un seul endroit.
 *
 * ⚠️ `<input type="date">` a été essayé d'abord, ici comme là : son apparence
 * est celle du navigateur, pas celle de l'application, et elle change d'un
 * poste à l'autre.
 */

/**
 * `AAAA-MM-JJ` à partir des composantes LOCALES de la date.
 *
 * ⚠️ Surtout pas `toISOString()`, qui convertit d'abord en UTC : une date
 * choisie le 12 revient le 11 pour tout utilisateur à l'est de Greenwich.
 */
export function versIso(d: Date): string {
  const mois = String(d.getMonth() + 1).padStart(2, '0')
  const jour = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mois}-${jour}`
}

export function ChampDate({
  label,
  value,
  onChange,
  min,
  max,
  jourFerme,
  error,
  helperText,
}: {
  label: string
  /** `AAAA-MM-JJ`, ou chaîne vide. */
  value: string
  onChange: (valeur: string) => void
  /** Bornes du calendrier — « today », une date, ou rien. */
  min?: string | Date
  max?: string | Date
  /**
   * Journées à griser — les jours de fermeture, par exemple.
   *
   * ⭐ Griser vaut mieux que laisser choisir : un dimanche cliquable qui ne
   * propose ensuite aucun créneau laisse croire à une panne. Le calendrier dit
   * ce qui est possible avant qu'on essaie.
   */
  jourFerme?: (d: Date) => boolean
  error?: boolean
  helperText?: string
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const fpRef = useRef<Instance | null>(null)
  // `onChange` est recréé à chaque rendu du parent : le garder dans une
  // référence évite de détruire et reconstruire le calendrier à chaque frappe
  // ailleurs dans le formulaire.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const fermeRef = useRef(jourFerme)
  fermeRef.current = jourFerme

  useEffect(() => {
    if (!inputRef.current) return

    const instance = flatpickr(inputRef.current, {
      locale: French,
      dateFormat: 'd/m/Y',
      ...(min !== undefined ? { minDate: min } : {}),
      ...(max !== undefined ? { maxDate: max } : {}),
      // ⚠️ Lu via une référence : la fonction est recréée à chaque rendu du
      // parent, et la capturer directement figerait les horaires du premier
      // rendu — un changement d'horaires ne se verrait qu'au rechargement.
      ...(jourFerme ? { disable: [(d: Date) => fermeRef.current?.(d) ?? false] } : {}),
      // La saisie au clavier reste possible : sur un poste de caisse, taper
      // « 12/03/1988 » va plus vite que quinze clics de calendrier.
      allowInput: true,
      onChange: (dates) => {
        onChangeRef.current(dates[0] ? versIso(dates[0]) : '')
      },
    })
    fpRef.current = instance

    return () => {
      instance.destroy()
      fpRef.current = null
    }
    // ⚠️ Dépendances VIDES à dessein : les bornes ne changent pas en cours de
    // vie du champ, et les inclure ferait détruire puis reconstruire le
    // calendrier à chaque rendu du parent — donc à chaque frappe ailleurs dans
    // le formulaire.
  }, [])

  // Valeur poussée de l'extérieur — ouverture de la fiche, remise à zéro du
  // formulaire. `false` : ne pas rejouer `onChange`, ce qui bouclerait.
  useEffect(() => {
    const fp = fpRef.current
    if (!fp) return
    const courante = fp.selectedDates[0] ? versIso(fp.selectedDates[0]) : ''
    if (courante === value) return

    /**
     * ⚠️ Un objet `Date`, jamais la chaîne « AAAA-MM-JJ ».
     *
     * `setDate` interprète une chaîne selon `dateFormat`, qui vaut ici
     * « d/m/Y » : « 2026-09-04 » y est illisible, et le champ restait VIDE
     * alors que la valeur était bien fournie. Le défaut ne se voyait pas tant
     * que le champ s'ouvrait vide — cas de la date de naissance, presque
     * toujours à saisir — et il est apparu le jour où l'agenda a proposé une
     * date par défaut.
     *
     * ⚠️ Midi, et non minuit : construire la date à minuit la ferait basculer
     * la veille pour tout utilisateur à l'ouest de Greenwich.
     */
    fp.setDate(value ? new Date(`${value}T12:00:00`) : '', false)
  }, [value])

  return (
    <TextField
      label={label}
      placeholder="JJ/MM/AAAA"
      inputRef={inputRef}
      error={error}
      helperText={helperText}
      InputLabelProps={{ shrink: true }}
    />
  )
}
